import { monthKeyFromT, parseMonthEnd } from '../engine/calendar';
import type { DriverParams, DriverType } from '../engine/types';
import { driverInputUnit } from '../driverUtils';
import {
    formatCurrency,
    formatMonthLabel,
    formatNumber,
    formatPercent,
    formatSummaryMoney,
} from '../projectionsFormat';

export type ShapeKind = 'flat' | 'step' | 'ramp' | 'seasonal' | 'curve';

export interface ShapeSegment {
    from_month: string;
    to_month: string;
    input_value: number | null;
    input_rendered: string;
}

export interface ShapeChange {
    month: string;
    from_rendered: string;
    to_rendered: string;
    direction: 'up' | 'down' | 'none';
}

export interface ShapeRamp {
    from_rendered: string;
    to_rendered: string;
    per_month_rendered: string;
}

export interface DetectShapeResult {
    shape: ShapeKind;
    segments: ShapeSegment[];
    changes: ShapeChange[];
    ramp: ShapeRamp | null;
    summary_text: string;
}

type RunSegment = [number, number, number | null];

function runLengthSegments(values: (number | null)[]): RunSegment[] {
    if (values.length === 0) {
        return [];
    }
    const segments: RunSegment[] = [];
    let start = 0;
    let current = values[0];
    for (let idx = 1; idx < values.length; idx += 1) {
        if (values[idx] !== current) {
            segments.push([start, idx - 1, current ?? null]);
            start = idx;
            current = values[idx];
        }
    }
    segments.push([start, values.length - 1, current ?? null]);
    return segments;
}

function pearsonAutocorr(series: number[]): number {
    if (series.length < 13) {
        return 0;
    }
    const a = series.slice(12);
    const b = series.slice(0, -12);
    if (a.length !== b.length || a.length === 0) {
        return 0;
    }
    const meanA = a.reduce((s, v) => s + v, 0) / a.length;
    const meanB = b.reduce((s, v) => s + v, 0) / b.length;
    let num = 0;
    let denA = 0;
    let denB = 0;
    for (let i = 0; i < a.length; i += 1) {
        const da = a[i]! - meanA;
        const db = b[i]! - meanB;
        num += da * db;
        denA += da * da;
        denB += db * db;
    }
    if (denA === 0 || denB === 0) {
        return 0;
    }
    return num / (Math.sqrt(denA) * Math.sqrt(denB));
}

function isMonotone(series: number[]): boolean {
    if (series.length < 2) {
        return true;
    }
    const inc = series.every((v, i) => i === 0 || series[i - 1]! <= v);
    const dec = series.every((v, i) => i === 0 || series[i - 1]! >= v);
    return inc || dec;
}

function diffCv(series: number[]): number {
    if (series.length < 2) {
        return Infinity;
    }
    const diffs: number[] = [];
    for (let i = 1; i < series.length; i += 1) {
        diffs.push(series[i]! - series[i - 1]!);
    }
    const mean = diffs.reduce((s, v) => s + v, 0) / diffs.length;
    if (mean === 0) {
        return Infinity;
    }
    const variance =
        diffs.reduce((s, v) => s + (v - mean) ** 2, 0) / diffs.length;
    const std = Math.sqrt(variance);
    return Math.abs(std / mean);
}

function direction(a: number, b: number): 'up' | 'down' | 'none' {
    if (b > a) {
        return 'up';
    }
    if (b < a) {
        return 'down';
    }
    return 'none';
}

function inputStyle(driverType: DriverType, unit: 'usd' | 'count' | 'pct'): 'pct' | 'count' | 'money' {
    if (
        driverType === 'mom_pct' ||
        driverType === 'yoy_pct' ||
        driverType === 'pct_gross_sales' ||
        driverType === 'pct_gross_sales_growth' ||
        driverType === 'pct_of_line'
    ) {
        return 'pct';
    }
    if (driverType === 'dio' || driverType === 'dso' || driverType === 'dpo') {
        return 'count';
    }
    if (unit === 'pct') {
        return 'pct';
    }
    if (unit === 'count') {
        return 'count';
    }
    return 'money';
}

function renderInput(
    value: number | null,
    driverType: DriverType,
    unit: 'usd' | 'count' | 'pct',
): string {
    if (value == null || !Number.isFinite(value)) {
        return '—';
    }
    const style = inputStyle(driverType, unit);
    if (style === 'money') {
        const abs = Math.abs(value);
        if (abs < 10_000) {
            return formatCurrency(value);
        }
        return formatSummaryMoney(value);
    }
    if (style === 'pct') {
        return formatPercent(value);
    }
    return formatNumber(value);
}

function horizonMonthKeys(lastClosedMonth: string, nMonths: number): string[] {
    const months: string[] = [];
    for (let t = 1; t <= nMonths; t += 1) {
        months.push(monthKeyFromT(lastClosedMonth, t));
    }
    return months;
}

function changesFromRle(
    rle: RunSegment[],
    months: string[],
    driverType: DriverType,
    unit: 'usd' | 'count' | 'pct',
): ShapeChange[] {
    const changes: ShapeChange[] = [];
    for (let idx = 1; idx < rle.length; idx += 1) {
        const prevVal = rle[idx - 1]![2];
        const currVal = rle[idx]![2];
        const boundaryMonth = months[rle[idx]![0]]!;
        changes.push({
            month: boundaryMonth,
            from_rendered: renderInput(prevVal, driverType, unit),
            to_rendered: renderInput(currVal, driverType, unit),
            direction: direction(prevVal ?? 0, currVal ?? 0),
        });
    }
    return changes;
}

function topChangesFromRle(
    rle: RunSegment[],
    months: string[],
    driverType: DriverType,
    unit: 'usd' | 'count' | 'pct',
): ShapeChange[] {
    const allChanges = changesFromRle(rle, months, driverType, unit);
    const scored: Array<{ delta: number; change: ShapeChange }> = [];
    for (let idx = 1; idx < rle.length; idx += 1) {
        const prevVal = rle[idx - 1]![2] ?? 0;
        const currVal = rle[idx]![2] ?? 0;
        scored.push({ delta: Math.abs(currVal - prevVal), change: allChanges[idx - 1]! });
    }
    scored.sort((a, b) => b.delta - a.delta);
    return scored.map((item) => item.change);
}

function buildSummaryText(
    shape: ShapeKind,
    driverType: DriverType,
    unit: 'usd' | 'count' | 'pct',
    horizonVals: (number | null)[],
    changes: ShapeChange[],
    ramp: ShapeRamp | null,
    params: DriverParams,
): string {
    if (shape === 'curve') {
        const start = renderInput(
            typeof params.start === 'number' ? params.start : null,
            driverType,
            unit,
        );
        const end = renderInput(
            typeof params.end === 'number' ? params.end : null,
            driverType,
            unit,
        );
        return `Curve ${start} → ${end}`;
    }
    if (shape === 'flat') {
        const v = horizonVals.find((x) => x != null) ?? horizonVals[0];
        return `Flat ${renderInput(v ?? null, driverType, unit)}`;
    }
    if (shape === 'step' && changes.length > 0) {
        const ch = changes[0]!;
        return `Step: ${ch.from_rendered} → ${ch.to_rendered} at ${formatMonthLabel(ch.month)}`;
    }
    if (shape === 'ramp' && ramp) {
        return `Ramp ${ramp.from_rendered} → ${ramp.to_rendered}`;
    }
    if (shape === 'seasonal') {
        const nums = horizonVals.filter((v): v is number => v != null && Number.isFinite(v));
        if (nums.length === 0) {
            return 'Seasonal';
        }
        const min = Math.min(...nums);
        const max = Math.max(...nums);
        return `Seasonal ${renderInput(min, driverType, unit)}–${renderInput(max, driverType, unit)}`;
    }
    return shape;
}

export function summaryHorizonMonths(lastClosedMonth: string): number {
    const closed =
        lastClosedMonth.length === 7
            ? parseMonthEnd(`${lastClosedMonth}-01`)
            : parseMonthEnd(lastClosedMonth);
    const lastYear = closed.getFullYear() + 1;
    const lastKey = `${lastYear}-12`;
    let count = 0;
    for (let t = 1; t <= 36; t += 1) {
        const mk = monthKeyFromT(lastClosedMonth, t);
        if (mk <= lastKey) {
            count += 1;
        } else {
            break;
        }
    }
    return count;
}

export function seasonalityFromHistory(options: {
    valuesByMonth: Record<string, number | null>;
    years: number;
}): {
    factors: Record<string, number> | null;
    skipped_reason?: string;
} {
    const { valuesByMonth, years } = options;
    const monthFactors: number[][] = Array.from({ length: 12 }, () => []);

    const allMonths = Object.keys(valuesByMonth).sort();
    if (allMonths.length === 0) {
        return { factors: null, skipped_reason: 'No complete calendar year of actuals' };
    }

    const yearsFound = new Set<number>();
    for (const mk of allMonths) {
        yearsFound.add(Number(mk.slice(0, 4)));
    }
    const sortedYears = [...yearsFound].sort((a, b) => b - a);

    let completeYears = 0;
    for (const year of sortedYears) {
        if (completeYears >= years) {
            break;
        }
        const yearVals: number[] = [];
        let valid = true;
        for (let m = 1; m <= 12; m += 1) {
            const mk = `${year}-${String(m).padStart(2, '0')}`;
            const v = valuesByMonth[mk];
            if (v == null || !Number.isFinite(v) || v === 0) {
                valid = false;
                break;
            }
            yearVals.push(v);
        }
        if (!valid) {
            continue;
        }
        completeYears += 1;
        const mean = yearVals.reduce((s, v) => s + v, 0) / 12;
        for (let m = 0; m < 12; m += 1) {
            monthFactors[m]!.push(yearVals[m]! / mean);
        }
    }

    if (completeYears < 1) {
        return { factors: null, skipped_reason: 'No complete calendar year of actuals' };
    }

    const factors: Record<string, number> = {};
    let sum = 0;
    for (let m = 1; m <= 12; m += 1) {
        const arr = monthFactors[m - 1]!;
        const avg = arr.reduce((s, v) => s + v, 0) / arr.length;
        factors[String(m)] = avg;
        sum += avg;
    }
    const norm = sum / 12;
    for (let m = 1; m <= 12; m += 1) {
        factors[String(m)] = factors[String(m)]! / norm;
    }
    return { factors };
}

export function detectShape(
    values36: (number | null)[],
    driverType: DriverType,
    horizonMonths: number,
    options?: {
        lastClosedMonth?: string;
        unit?: 'usd' | 'count' | 'pct';
        params?: DriverParams;
    },
): DetectShapeResult {
    const params = options?.params ?? {};
    const unit = options?.unit ?? driverInputUnit(driverType as Parameters<typeof driverInputUnit>[0]);
    const lastClosed = options?.lastClosedMonth ?? '';
    const months = lastClosed ? horizonMonthKeys(lastClosed, horizonMonths) : [];
    const horizonVals = values36.slice(0, horizonMonths);

    if (driverType === 's_curve') {
        return {
            shape: 'curve',
            segments: [],
            changes: [],
            ramp: null,
            summary_text: buildSummaryText('curve', driverType, unit, horizonVals, [], null, params),
        };
    }

    const rle = runLengthSegments(horizonVals);
    const segments: ShapeSegment[] = rle.map(([fromIdx, toIdx, val]) => ({
        from_month: months[fromIdx] ?? String(fromIdx + 1),
        to_month: months[toIdx] ?? String(toIdx + 1),
        input_value: val,
        input_rendered: renderInput(val, driverType, unit),
    }));

    if (driverType === 'yoy_pct' || driverType === 'yoy_dollar') {
        return {
            shape: 'seasonal',
            segments,
            changes: [],
            ramp: null,
            summary_text: buildSummaryText('seasonal', driverType, unit, horizonVals, [], null, params),
        };
    }

    if (rle.length === 1) {
        return {
            shape: 'flat',
            segments,
            changes: [],
            ramp: null,
            summary_text: buildSummaryText('flat', driverType, unit, horizonVals, [], null, params),
        };
    }

    if (rle.length >= 2 && rle.length <= 4) {
        const changes = changesFromRle(rle, months, driverType, unit).slice(0, 3);
        return {
            shape: 'step',
            segments,
            changes,
            ramp: null,
            summary_text: buildSummaryText('step', driverType, unit, horizonVals, changes, null, params),
        };
    }

    const decNonNull = horizonVals.filter((v): v is number => v != null && Number.isFinite(v));
    if (decNonNull.length === horizonVals.length && isMonotone(decNonNull)) {
        const cv = diffCv(decNonNull);
        if (cv < 0.25) {
            const first = decNonNull[0]!;
            const last = decNonNull[decNonNull.length - 1]!;
            const per = (last - first) / (decNonNull.length - 1);
            const ramp: ShapeRamp = {
                from_rendered: renderInput(first, driverType, unit),
                to_rendered: renderInput(last, driverType, unit),
                per_month_rendered: renderInput(per, driverType, unit),
            };
            return {
                shape: 'ramp',
                segments,
                changes: [],
                ramp,
                summary_text: buildSummaryText('ramp', driverType, unit, horizonVals, [], ramp, params),
            };
        }
    }

    if (decNonNull.length > 0 && pearsonAutocorr(decNonNull) >= 0.7) {
        return {
            shape: 'seasonal',
            segments,
            changes: [],
            ramp: null,
            summary_text: buildSummaryText('seasonal', driverType, unit, horizonVals, [], null, params),
        };
    }

    const changes = topChangesFromRle(rle, months, driverType, unit).slice(0, 3);
    return {
        shape: 'step',
        segments,
        changes,
        ramp: null,
        summary_text: buildSummaryText('step', driverType, unit, horizonVals, changes, null, params),
    };
}
